import { useEffect, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Flame, Eye, ArrowUpRight, Mail } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────
   EngagementAlertsListener — mount once at the app shell.

   Polls /api/engagement/recent-alerts every 30s, dedupes against seen
   alerts in sessionStorage, and fires a toast for each new signal.
   ───────────────────────────────────────────────────────────────────── */

const SEEN_KEY = "engagement_alerts_seen_v1";

function readSeen() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
  } catch { return new Set(); }
}
function writeSeen(set) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set).slice(-200)));
  } catch { /* noop */ }
}

const ICON = {
  deep_dwell:     <Eye className="w-4 h-4" />,
  revisit:        <ArrowUpRight className="w-4 h-4" />,
  progress_email: <Mail className="w-4 h-4" />,
};

function titleFor(a) {
  switch (a.type) {
    case "deep_dwell":
      return `${a.client_name || "A client"} lingered ${Math.round((a.dwell_ms || 0) / 1000)}s on "${a.slide_title}"`;
    case "revisit":
      return `${a.client_name || "A client"} revisited "${a.proposal_name}"`;
    case "progress_email":
      return `Progress email sent to ${a.to}`;
    default:
      return "Engagement signal";
  }
}

export default function EngagementAlertsListener() {
  const seenRef = useRef(readSeen());
  const firstRunRef = useRef(true);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const res = await api.get("/engagement/recent-alerts");
        const alerts = res.data?.alerts || [];
        if (!alive) return;
        // First run: seed seen without firing toasts (avoid spam on app open).
        if (firstRunRef.current) {
          for (const a of alerts) seenRef.current.add(`${a.type}:${a.proposal_id}:${a.timestamp}`);
          writeSeen(seenRef.current);
          firstRunRef.current = false;
          return;
        }
        // Subsequent runs: fire toasts for truly new alerts
        const newly = [];
        for (const a of alerts) {
          const k = `${a.type}:${a.proposal_id}:${a.timestamp}`;
          if (!seenRef.current.has(k)) { newly.push(a); seenRef.current.add(k); }
        }
        if (newly.length) writeSeen(seenRef.current);
        for (const a of newly.slice(-5)) {
          toast(titleFor(a), {
            icon: ICON[a.type] || <Flame className="w-4 h-4" />,
            description: "Open the pitch →",
            duration: 8000,
            action: {
              label: "Open",
              onClick: () => { window.location.href = `/proposal-builder/${a.proposal_id}/pitch`; },
            },
          });
        }
      } catch {
        /* silently ignore poll failures */
      }
    };
    poll();
    const t = setInterval(poll, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return null;
}
